// What a site's device rows say about each device, in words (#830, #878): its
// status, what it is running, where Zenith's last write to it got to, where its
// configuration came from and how many clients it reports — the vocabulary the
// two per-family lists (`FamilyTables.tsx`), the device page's head and its
// detail all draw from, so one fact is one set of words wherever it appears.
// Online comes from the gateway's session record and nothing else (ZENITH.md
// §9.4) — read on a list from Zenith's dated copy of the cell's listing
// (ADR-0234, §9.5) and on a device's own page live; reports are read live and
// never stored. The table these once belonged to split into two lists in #878.
import { useCallback, useRef } from "react";
import { api, unattended } from "../../api/client";
import { shared } from "../../api/writes";
import { SKIP, UNATTENDED, useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";
import { CarriedMark, exitOf, exitTone, type ExitReading, type useCarried } from "../../carried";
import { ScopeLink } from "../../shell/scope";
import { Pill } from "../../ui";
import { absentExplain, isAbsent, type Device } from "./commands";
import { firstBy } from "./firstBy";
import { listed, unrecognized, wordFor } from "../../api/openEnum";

// The named lists: since ADR-0511 each response field is its list or any other string.
type ConvergenceState = components["schemas"]["DeviceConvergenceState"];

/** The colour a state word carries beside it, named rather than a CSS value
 * so the table's tag and the device page's pill (#329) draw the same word the
 * same way. A word with no tone is muted: nothing to signal. */
export type Tone = "good" | "warn" | "critical";

/** Whether a state is drawn at all, or is only said (#830).
 *
 * **Only a state an operator has to do something about is marked.** `online`,
 * `delivered` and `current` were each a coloured tag, so a healthy site drew
 * seventeen of them across six rows and the one row carrying a question — a
 * push the cell never answered — was the same size and shape as the sixteen
 * that carried none. Colour that is on everything is not a signal, and at the
 * 200-row page size it is not a degradation but the whole experience.
 *
 * So `good` is unmarked and reads as quiet type; `warn` and `critical` are
 * marked. They are marked with the house pill rather than the flat tag,
 * because the pill carries a dot as well as a colour — `theme.css` says of it
 * "never colour alone" — and that dot is the only non-colour channel a
 * deuteranopic reader has here: `.tag`'s is the dashed border, and `tag-off`
 * has already spent it on "this word has no tone".
 *
 * **No word and no tooltip changes, and no tone moves.** The tones are
 * `statusOf`'s, `PUSH`'s and `CONVERGENCE`'s exactly as they were; what moved
 * is only which of them is painted. In particular `offline` stays untoned and
 * therefore unmarked, which is ADR-0040's reading and not this change's: a
 * device the cell has and is not talking to comes back, and the Status word
 * says so without the table shouting it. */
function isMarked(tone?: Tone): tone is "warn" | "critical" {
  return tone === "warn" || tone === "critical";
}

/** A state in words: the word, its tone, and what a hover says about it. */
export interface StateWords { word: string; tone?: Tone; title?: string }

/** A site's convergence, read once for the page and joined on the serial.
 *
 * A failed read returns no data rather than an empty device list: an empty
 * list would render every row as "—", which reads as "nothing to say" when
 * the truth is "we could not ask". */
// One read for every asker at once, as the work list's (`useWorkListRead`,
// #1811): the shell follows an approval with this same read, so a page that
// opens while the shell's read is out joins it. `unattended` marks every read
// as asked by nobody, which is what the shell's are.
export function useConvergence(siteId: string | undefined, opts: { unattended?: boolean } = {}) {
  const all = !!opts.unattended;
  const asked = useRef(false);
  const q = useQuery((how) => {
    // A person's own reload is use even where every other read is not.
    const mark = { unattended: all ? !asked.current : how.unattended };
    asked.current = false;
    return siteId
      ? shared(`convergence:${siteId}`, () => api.GET("/sites/{id}/convergence", { params: { path: { id: siteId }, header: unattended(mark) } }))
      : SKIP;
  }, [siteId, all]);
  const { reload: again } = q;
  const reload = useCallback((m?: unknown) => { asked.current = m !== UNATTENDED; again(m); }, [again]);
  return { ...q, reload };
}

/** The stations the site's access points report, read once for the page and
 * joined on the serial (#248, ZENITH.md §9.5): one bounded read for the
 * table, not one per row. The same rule as convergence: a failed read is no
 * data, so the column stays blank rather than showing every access point as
 * having nobody on it. */
export function useClients(siteId: string | undefined) {
  return useQuery(() => siteId
    ? api.GET("/sites/{id}/clients", { params: { path: { id: siteId } } })
    : SKIP, [siteId]);
}

/** The device's state, under its name in the frozen identity cell (#830).
 *
 * **Status keeps a visual indicator even when it is nominal, which the rest of
 * the row does not.** The marking rule above stops painting a state nobody has
 * to act on, and applied to Status alone it goes too far: whether the cell is
 * talking to this device is the thing an operator is looking for when they open
 * this page, so it gets a dot at every state — green for `online`, muted for
 * `offline`, and the critical pill for `absent`. The word is unchanged and so
 * is its tooltip; what the dot buys is that the column can be read down rather
 * than word by word.
 *
 * It is a dot and not a pill because a pill per row is the seventeen-lozenge
 * problem this pass exists to remove. One small mark, in the frozen cell, beside
 * the name it belongs to. */
export function StatusState({ device }: { device: Device }) {
  return <StateDotted words={statusOf(device)} />;
}

/** The dotted rendering the paragraph above argues for, as its own component so
 * that a second view of the same fact cannot draw it differently.
 *
 * Extracted for #865: the superuser's *Not yet on any site* panel renders
 * §9.4's `online` too, and it drew that fact as quiet type with no dot while
 * the table beside it drew the same fact with one. Two renderings of one
 * definition is the shape §9.4 forbids arriving through the styling — which is
 * the argument #830 already made, in the other direction, about a green pill. */
export function StateDotted({ words: w }: { words: StateWords }) {
  if (isMarked(w.tone)) return <State words={w} />;
  return (
    <span className="state-dotted" title={w.title}>
      <span className={"state-dot" + (w.tone === "good" ? " state-dot-good" : "")} aria-hidden="true" />
      <span className="muted">{w.word}</span>
    </span>
  );
}

/** The device's state in one tag, with `absent` as its own word.
 *
 * **Three words and not two** (ADR-0040): `online` and `offline` are what the
 * gateway says about a device it has, and `absent` is the cell saying it does
 * not have the device at all — which only a switch can be, because a switch's
 * site membership is Zenith's own record and outlives the cell's copy. Drawing
 * that as "offline" is what this replaces: it told an operator to wait for a
 * device that was never coming back, and the row's only real move — the
 * unclaim — looked like giving up on a device that might return.
 *
 * A switch whose `presence` the cell did not answer for carries neither word
 * and falls through to online/offline, which is what the field's absence
 * means. */
// Restates ADR-0040 — `absent` is the cell saying it does not have the device at all, which
// only a switch can be; the comment directly above carries the argument. The other two words
// are onlineOf's and their marker is on it, because web/README.md puts the marker where the
// StateWords value is and #865 moved them there.
export function statusOf(device: Device): StateWords {
  if (isAbsent(device)) return { word: "absent", tone: "critical", title: absentExplain(device) };
  return onlineOf(device.online_state);
}

/** Status in the order an operator reads it down: up, then down, then the two
 * the cell cannot vouch for. The rank is of `statusOf`'s word, which is read
 * from `online_state` and `presence` as served — nothing is derived here.
 *
 * Beside `statusOf` so that every list sorting on the word sorts in one order:
 * the site's family tables and the customer's list (#1265) both take it from
 * here, and a second table would let `unknown` sit with `offline` on one page
 * and apart from it on the other. */
export const STATUS_RANK: Record<string, number> = { online: 0, offline: 1, unknown: 2, absent: 3 };

/** §9.4's `online`, in words, for every view that renders it.
 *
 * **One definition of online gets one set of words.** `statusOf` is the table's
 * caller and the unassigned panel is the other (#865); a third would come here
 * rather than write its own sentence, which is what stopped the two from
 * agreeing in the first place. The strings are bounded wording, not prose: the
 * marker below argues each clause from one of ADR-0097's decisions — read it
 * before changing a word of them.
 *
 * `absent` is not here on purpose: it is a fact about whether the **cell** has
 * the device at all (ADR-0040), and it cannot arise for a record read out of
 * the cell's own unassigned inventory. */
// Restates ADR-0097 decisions 1, 2 and 5, ZENITH.md §9.4 — `online` is §9.4's
// one definition and nothing else's, and since ADR-0097 that definition is **two
// conditions**: the gateway holds a session, and its last contact has not aged past
// `ONLINE_CONTACT_STALENESS`. The first on its own is what owgw answered `connected: true`
// with for 607-610 s after an access point lost power, which is why the sentence no longer
// calls online "the gateway's word".
//
// **The wording is bounded by three of that ADR's decisions and by §29, in this order:**
//
//  1. **Decision 5.** A live session whose contact stamp is unusable is `online` — the one
//     place the definition does not fail closed, because `offline` refuses the device's
//     firmware upgrade. So the copy says the last contact has not *aged past* the bound,
//     which is true when nothing has aged, and never that the device has been heard from,
//     which would be false for that case. It is one word of difference and it is the whole
//     reason this phrasing and not the obvious one.
//  2. **Decision 1, and ADR-0219 is what moved it.** ADR-0097 decision 1 added no word, and
//     this clause used to say the six were unchanged and that there was no seventh state.
//     **There is now, and it is `unknown`** (ADR-0219, ruled on #1200): a device the cell
//     holds no record for. `offline` is a claim an operator acts on — they wait, or they go
//     to the site — and it was the claim this function made for 21 access points that were
//     all up, because the bulk gateway listing carried none of their serials for one read.
//     The seventh word is the ruling, not a drift from decision 1: decision 1 forbade a
//     qualifier beside `online`, and this is a distinct state beside `offline`, which is the
//     shape ADR-0040 already gave a switch in `absent`.
//  3. **§29.** `ONLINE_CONTACT_STALENESS` is open for production, so the copy says a bound
//     exists and never what it is. A figure here would be a §29 value in front of a customer.
//
// **Re-read under ADR-0234 (#1305), and it holds.** A list's `online_state` is judged from
// Zenith's copy of the cell's listing — the session the copy holds, and a last contact that
// has not aged past the bound, judged at the time of the answer — so the sentence is true of
// a copy as it is of a live read. What a copy adds is an age, and that is said once for the
// whole screen, in the shell's footer under the build (`shell/freshness.tsx`, #1315), and
// not here.
//
// This marker sat above StatusState until #865, which is where these words were. They are
// here now, and so is it: web/README.md's rule is that the marker sits on the copy and a
// StateWords value is where the words are. `absent` stayed behind with statusOf, which is
// the only caller that can produce it.
//
// `offline` is **unchanged in its words and narrowed in its reach**: "the cell has this
// device and is not talking to it" is true of both shapes — a session the gateway dropped,
// and one it holds and has stopped hearing on — and decision 1 rules out adding a qualifier
// to say which. What it is no longer said of is a device the cell holds no record for, which
// is `unknown` (ADR-0219); the sentence was already false of that case and nothing said so.
// `absent` is the cell not having a **switch** it has a Zenith row for (ADR-0040); `unknown`
// is the access-point-and-switch form of the same absence, read from the gateway rather than
// from `presence`.
//
// Restates ADR-0219 decisions 1 and 3, ADR-0234 decisions 3 (`3c`) and 4 (`4a`), ADR-0295 — absence of a
// gateway record is a third state and not a kind of offline, and the disposition is read from
// `online_state` and never derived here. On a list the record is Zenith's copy of the cell's
// listing, so `unknown` there also covers a device that arrived after the copy was taken,
// which the next refresh lists and whose own page already reads live; the sentence says so,
// because the repair for that case is no repair.
export function onlineOf(state: string): StateWords {
  switch (state) {
    case "online":
      return { word: "online", tone: "good", title: "The controller holds a session for this device and its last contact has not aged past the staleness bound — both, not either. The session record outlives the device, so on its own it is not a claim that the device is up." };
    // Restates ADR-0040 — offline is a device the cell holds and is not hearing from;
    // absent is one the cell does not hold at all.
    case "offline":
      return { word: "offline", title: "The controller has this device and is not talking to it; it comes back. Not the same as the controller not holding it." };
    case "unknown":
      return { word: "unknown", tone: "warn", title: "The controller holds no record for this device — in the copy of its listing kept here, on a list, or at this read on the device's own page — so nothing is known about whether it is up. This is not offline and is not waited out: the device has never connected to this controller, its claim did not complete, or it arrived after the copy was taken. The first two are repaired at the controller; the third is on the list after the next refresh, and its own page already reads live." };
    // Restates ADR-0511 decision 5a, ADR-0219 — a state this build does not know is shown as
    // received and marked, and is never read as offline or as `unknown`, which is the controller
    // holding no record.
    default:
      return { word: unrecognized(state), tone: "warn", title: "This page does not recognize the state the server gave for this device, so it cannot say whether the device is up. Reload the page: a newer version may know it." };
  }
}

/** The three values `online_state` can take. Named so a caller that has the
 * fact without a whole `Device` — the unassigned panel — passes the same
 * vocabulary rather than a boolean this function would have to widen again. */
export type LivenessState = components["schemas"]["DeviceOnlineState"];

/** A state in the table: the pill when it is marked, quiet type when it is not.
 * The tooltip is the same string either way, so nothing a hover said before is
 * lost by a word ceasing to be painted. */
export function State({ words: w }: { words: StateWords }) {
  if (isMarked(w.tone)) return <Pill tone={w.tone} title={w.title}>{w.word}</Pill>;
  return <span className="muted" title={w.title}>{w.word}</span>;
}

/** What a switch's Configuration says: whether Zenith manages its ports
 * (ADR-0074 decision 3). Read from `ports_managed`, which the API derives from
 * the site's port intent and never from `source` — a carried switch whose ports
 * Zenith manages stays carried until its release (ADR-0427), so a word
 * derived from the source could never change. Undefined when the read did not
 * say, which renders a dash rather than a guess. */
// Restates ADR-0074 decision 3, ADR-0057 — whether Zenith manages a switch's ports is
// the site's port intent and never `source`, and the rest of the switch's document stays
// its own because the port path merges rather than replacing. Restates ADR-0215 decision
// 5a — a first document sent from the switch's own read changes no `source`, and its
// ports are not Zenith's until they are imported, so this word is unchanged by it.
// Restates ADR-0239 decision 9b, ADR-0052 decision 6, ADR-0295 — `ports managed` is no longer a
// switch's word alone: it extends to an access point whose document carries its wired
// port intent, which the site's generated plan sends it as its own document (#1322).
// This function stays the switch's; an access point's word is accessPointPortsWords,
// reached from ConfigurationCell only for an access point that is not carried, because a
// carried one is sent nothing (decision 6b) and its column says its carried state.
// ADR-0069 decision 5 needs nothing: its family refusal was discharged by #744.
export function switchConfigurationOf(device: Device): StateWords | undefined {
  if (device.ports_managed === undefined) return undefined;
  const rest = "The rest of the switch's configuration is its own: the planned port settings are merged into it and do not replace it.";
  return device.ports_managed
    ? { word: "ports managed", title: `The site has planned port settings for this switch, so its ports are managed here. ${rest}` }
    : { word: "its own", title: `The site has no planned port settings for this switch, so its configuration is entirely its own. ${rest}` };
}

/** What a generated access point's Configuration says when the site holds port intent
 * for it (ADR-0239 decision 9b): the site's plan writes that intent into the access
 * point's own document — the venue's, with its wired ports written in — rather than
 * merging it into a document the device already holds, which is the switch's sense of
 * the same word. Undefined where the read did not say or the site holds none, which
 * leaves the access point's ordinary word. */
export function accessPointPortsWords(device: Device): StateWords | undefined {
  if (!device.ports_managed) return undefined;
  return {
    word: "ports managed",
    title: "The site has planned port settings for this access point, so its configuration is the one generated for the site with its wired ports written in, sent as its own configuration.",
  };
}

/** What a device's configuration came from (§11.2, ADR-0019 decision 4).
 *
 * The answers are different things and are said differently. A switch says
 * whether Zenith manages its ports (ADR-0074 decision 3), from the device row
 * and not from carry-over readiness, which judges access points only. A
 * carried access point shows its state and what it still needs. An access
 * point with no carried document is one Zenith generates for — that is
 * decision 4's fail-closed reading, where an absent record means generated and
 * the guard lives on the value. */
export function ConfigurationCell({ device, carried }: { device: Device; carried: ReturnType<typeof useCarried> }) {
  if (device.category === "switch") {
    const w = switchConfigurationOf(device);
    return w ? <span title={w.title}>{w.word}</span> : <span className="muted">—</span>;
  }
  const c = apConfigurationOf(device, carried);
  if (c.kind === "mark") {
    if (!c.exit) return <CarriedMark reason={c.reason} />;
    const o = exitOf(c.exit);
    return <><CarriedMark reason={c.reason} />{" "}<Pill tone={exitTone(o.again)} title={o.next}>{o.label}</Pill></>;
  }
  if (c.kind === "none") return <span className="muted">—</span>;
  return <span className={c.words.word === "ports managed" ? undefined : "muted"} title={c.words.title}>{c.words.word}</span>;
}

/** What `ConfigurationCell` draws for an access point, decided once: a carried
 * mark, a plain word, or nothing yet. The list's line of what every row shares
 * (UX-008) reads the plain word from here, so the line and the cell cannot
 * disagree about which word an access point carries. */
export function apConfigurationOf(device: Device, carried: ReturnType<typeof useCarried>):
  | { kind: "mark"; reason?: string; exit?: ExitReading } | { kind: "words"; words: StateWords } | { kind: "none" } {
  const serial = device.serial;
  const state = carried.data;
  if (!state) return { kind: "none" };
  const d = firstBy(state.devices, "serial").get(serial);
  if (d) {
    // Order matters. An unrecognised source is not carried and is emphatically
    // not Zenith's — reading as either would be the table asserting something
    // nobody knows, and "Zenith" is the dangerous one (#156).
    if (d.reason === "unrecognised_source") return { kind: "mark", reason: d.reason };
    if (!carried.carried.serials.has(serial)) {
      const ports = accessPointPortsWords(device);
      if (ports) return { kind: "words", words: ports };
      return { kind: "words", words: { word: "generated", title: "No carried configuration is recorded for this access point, so its configuration is the one generated for the site." } };
    }
    // #2249: a covered access point still carried says why, in the exit's own word.
    return { kind: "mark", reason: d.reason, exit: carried.carried.exits.get(serial) };
  }
  const ex = firstBy(state.excluded, "serial").get(serial);
  if (ex) return { kind: "words", words: { word: "not judged", title: ex.reason } };
  return { kind: "none" };
}

/** The word and the colour for each state the document declares. Typed
 * against the schema, so a state the document adds fails the typecheck
 * here rather than rendering its raw wire word (#193). */
// Restates ZENITH.md §11.2 and ADR-0504 decision 5a — the seven words and what each says,
// with `unknown` failing closed and `frozen` an access point left out of the site's last
// apply by name, held on what it was already served. The words here are the vocabulary's,
// and the document is where their meaning is ruled; a word whose meaning moves moves there first.
export const CONVERGENCE: Record<ConvergenceState, { label: string; tone?: Tone }> = {
  synchronized: { label: "current", tone: "good" },
  synchronizing: { label: "syncing", tone: "warn" },
  behind: { label: "behind", tone: "critical" },
  offline: { label: "offline" },
  carried: { label: "carried" },
  unknown: { label: "unknown", tone: "warn" },
  frozen: { label: "frozen", tone: "warn" },
};

/** The word for one convergence row: the state's, except where the held
 * document kept an access point from `behind`.
 *
 * Restates ADR-0253 decision 2a, as read by ADR-0337 (#1383) — an access point
 * whose controller holds a document Zenith did not send is `unknown` on the
 * wire and never re-fetched, and `held_against_sent: differs` says the document
 * was changed outside Zenith. The word names that rather than a bare
 * "unknown", because the operator's act is different: nothing is broken in
 * Zenith's record, someone else wrote the device, and applying the site's
 * configuration is the route back. `not_compared` keeps "unknown": Zenith
 * cannot tell, and saying more would be guessing. The tone is the state's. */
export function convergenceWord(d: { state: string; held_against_sent?: string }): { label: string; tone?: Tone } {
  // Restates ADR-0511 decision 5a — a state this build does not know is shown as received, marked,
  // and never as `unknown`.
  if (!listed(CONVERGENCE, d.state)) return { label: unrecognized(d.state), tone: "warn" };
  const c = CONVERGENCE[d.state];
  if (d.state === "unknown" && d.held_against_sent === "differs") return { label: "changed outside", tone: c.tone };
  return c;
}

/** Which configuration a device is running, against what the controller holds
 * for it (§11.2, ADR-0022 decision 3; for a switch, the generation its own
 * controller holds, ADR-0074 decision 1).
 *
 * It sits beside Configuration rather than replacing it because the two answer
 * different questions: that column says where the document came from, this one
 * says whether the device has taken it. A carried device is shown as carried in
 * both, which is not a repetition — it is the reason this column cannot say
 * anything else about it.
 *
 * `unknown` is rendered as a warning and never as a dash. It means the cell
 * said nothing, or said something this controller did not issue, and a blank
 * would read as "fine".
 *
 * "not judged" remains for a device the read excludes. Since ADR-0074 no
 * device this release reads is excluded, and a switch lands in `devices`. */
export function runningOf(serial: string, convergence: ReturnType<typeof useConvergence>): StateWords | undefined {
  const data = convergence.data;
  const d = data && firstBy(data.devices, "serial").get(serial);
  if (!d) {
    const ex = data && firstBy(data.excluded, "serial").get(serial);
    return ex ? { word: "not judged", title: ex.reason } : undefined;
  }
  // Restates ADR-0022 decision 3, ADR-0074 decision 1, ADR-0099, ADR-0108, ADR-0140 — the
  // two numbers are different facts and are named as such: what the device reports, and what
  // the controller holds.
  //
  // **"Reports" covers a declaration read in the controller's place** (ADR-0140): where the
  // device's own newest stored report declares a generation the controller does not hold,
  // `running` is that declaration rather than the status record's answer. The sentence stays
  // true — the device did say it — and which component said what is in `detail`, which is
  // prepended below. The convergence row carries no source flag of its own; `declared_running`
  // is on the diagnostics read, where an operator diagnosing one device looks. The outstanding push is the third, and it is what says the first
  // is not decisive.
  //
  // **An absent number is said, never defaulted (#722).** The API omits a generation to
  // mean the cell reported none — `handlers_config.go`: *"Zero is 'the cell said nothing',
  // not 'generation zero'"* — so a `?? 0` here renders an absence as the one number
  // decision 3 says must never be confused with it. Generation 0 is a claim with its own
  // causes (a factory reset, a replacement on the same serial, a device seeded and never
  // configured) and decision 6 rules it `behind` so the repair reaches it; the cell saying
  // nothing is not that, and one of them is the repair's own case. Where the cell reported
  // no generation at all the sentence is suppressed entirely and the word carries it
  // (ADR-0108), which is why a device with no controller record says nothing here.
  //
  // The generations are in the title rather than the cell: an operator reading
  // the table wants the word, and an operator diagnosing one device wants the
  // two numbers that produced it. A pending push is the third number when
  // there is one, since for a switch it is half of what `syncing` means.
  const reports = d.running !== undefined ? `Reports ${d.running}` : "The controller reported no running generation";
  const holds = d.held !== undefined ? `the controller holds ${d.held}` : "the controller holds none";
  const numbers = d.running !== undefined || d.held !== undefined
    ? ` ${reports}; ${holds}.${d.pending ? ` A push of ${d.pending} is outstanding.` : ""}`
    : "";
  const c = convergenceWord(d);
  // Restates ADR-0086 decisions 2 and 8 — the finding that the firmware stated for this device is
  // not the firmware it reports, and the condition that its configuration is not satisfied by the
  // firmware it runs, sit beside the state and never change its word. Where either is raised its
  // sentence follows the numbers, so the operator diagnosing one device reads it where the state is.
  const findings = [d.stated_firmware?.detail, d.unsatisfied?.detail].filter((x): x is string => !!x);
  const beside = findings.length > 0 ? " " + findings.join(" ") : "";
  return { word: c.label, tone: c.tone, title: (d.detail ?? "") + numbers + beside };
}


/** Where the last write Zenith made to this device got to (#413).
 *
 * **This is a different fact from the Running column beside it and must stay
 * one.** ZENITH.md §10.2: *applied* is a fact about the controller and
 * *synchronized* is a fact about a device. This says what Zenith sent and what
 * the cell answered; Running says what the controller's status record reports
 * the device running — which is the device's own answer only while nothing is
 * pending (ADR-0099).
 * Merging them would put back the conflation ADR-0022 exists to remove — a
 * device reading "synchronized" the moment its document was saved.
 *
 * The row comes from Zenith's own database and costs no controller call, which
 * is what keeps this column clear of §17.2's rule about the device list.
 *
 * `sending` is deliberately warned rather than shown as progress: after a run
 * has ended it means the answer was lost, which is the state #406 had no way to
 * express — a device the cell accepted read exactly like one never attempted.
 * A device that was never written to has no record at all and renders "—".
 *
 * **One more fact reaches the word since ADR-0485, and it is not the device's running state.**
 * A marked outcome the device has since settled past, or the unattended re-fetch's, is drawn
 * `settled` and neutral (`pushOf`), with the outcome in the tooltip. The column still says what
 * Zenith's last write got to, never what the device runs, so the two columns stay two (#835).
 */
// Keyed on the named list and not on the field: since ADR-0511 the field is the six or any string,
// which is `string`, and a map keyed on it would stop failing the typecheck for a missing state.
const PUSH: Record<components["schemas"]["DevicePushState"], { label: string; tone?: Tone; note?: string }> = {
  // Restates ZENITH.md §17.1 and §10.2, ADR-0022 decision 3, ADR-0074 decision 2, ADR-0094, ADR-0099, ADR-0485 — an apply
  // returns when the *controller* has accepted the document; what the device runs is reported
  // per device and read from the device's own answer, never from the controller's record of it.
  // ADR-0074 decision 2 is on this line because the "not by itself" qualifier below is argued
  // from it: change what resolves a switch push and this sentence is the copy to re-read.
  //
  // **The word said `delivered` until #1202, which is a claim about the device.** This pill is
  // drawn unlabelled beside the convergence pill in the device head, so "delivered" in green sat
  // next to "synchronized" with nothing saying they are different facts — and they are the two
  // halves §10.2 separates. The operator was told the strong claim and Zenith only ever held the
  // weak one: owgw's `pending`/`completed` is not read, and a `results.status.error` of 0 was
  // measured accompanying three management services being opened on a switch, confirmed at its
  // console, with nothing in the answer saying so (#666). Naming the cell in the word is what
  // makes the pill honest on its own, since a tooltip is not read before a colour is.
  // **That citation read #351 until ADR-0223 decision 4 (`4a`).** #351's own document concludes
  // its two apply errors were noise and owgw's `error: 0` honest, so it did not establish what it
  // was cited for; #666 does, and the claim is unchanged.
  //
  // Restates ADR-0223 decision 2, ruled `2c` on #1225, ADR-0295 — the label stays the weak half for every
  // row and the strong reading reaches the operator through the tooltip, never the word or the
  // colour. **ADR-0485 does not touch this row**: it re-draws only a marked outcome (`failed`,
  // `refused`, `unknown`, `sending`) that no longer describes the device, and `accepted` is not one.
  //
  // **The sentence says "not by itself", and that qualifier is load-bearing.** One `accepted` row
  // in four is genuinely strong: ADR-0074 decision 2 moves a `switch_ports` push from `unknown`
  // to `accepted` only on the switch reporting it runs the document Zenith sent, and the ADR says
  // that is "what `accepted` has always been read to mean for a switch".
  //
  // **The API now says which, and the label still does not** — which is the decision rather than
  // an omission. `resolved_at` is present exactly on a row a resolution moved (ADR-0223 decision
  // 1, `1b`), so `pushOf` below appends the strong sentence to the **tooltip** for those rows.
  // The word and the tone are unchanged for all four, because `2b` — a second word on the pill —
  // would be a seventh device word where ADR-0097 decision 1 said there were six, for a
  // distinction that changes no operator action: the pill is `good` either way and the action is
  // the same. `2a`, changing nothing on screen, was rejected for leaving the record unused after
  // decision 1 paid for it.
  //
  // **So this note is kept rather than removed, deliberately** (#1225's third condition): the
  // label understates a resolved switch push on purpose, and the tooltip is where the
  // understatement is lifted.
  //
  // **`web/README.md`'s *A word an Accepted ADR rules is never re-worded by a copy edit* was
  // read against this, and the reading is that it does not refuse it — but it is close enough
  // that it belongs in a comment rather than in nobody's head.** `ADR-0074`'s title and its
  // decision 2 heading both say an unanswered switch push *"resolves only to delivered"*, so
  // `delivered` is that ADR's word for this state and not an incidental label. What the rule
  // forbids is re-wording such a word — `held` and `running` are its examples, and ADR-0078
  // rules those as a pair of terms to be used in place of one another. This **keeps**
  // `delivered` and qualifies it; it removes nothing from the vocabulary and renames no state.
  // A change to `sent`, or to anything without `delivered` in it, would be the ADR the rule
  // means. **If a reviewer reads it the other way, the fix is the label alone** — the tooltip,
  // the contract and `pushOutcome`'s comment are additive and settle the defect either way.
  accepted: {
    label: "delivered to the controller",
    tone: "good",
    note: "The controller took the request. That is the record kept here of the send, and not by itself the device reporting that it runs this — the Running state is what says that.",
  },
  sending: { label: "sending", tone: "warn" },
  refused: { label: "refused", tone: "critical" },
  failed: { label: "failed", tone: "critical" },
  // #512: the cell had the request and did not answer in time, so the device
  // may be running it. Warned rather than critical, because nothing is known
  // to have gone wrong.
  unknown: { label: "no answer", tone: "warn" },
  // Restates ADR-0227 decision 5a — a record write's 200 is owgw holding bytes for a device that
  // has never connected, so it is never `accepted` and never green on its send. It is promoted by
  // resolution on the cell's own report that the device runs the generation the record was
  // stamped with, and `resolved_at` then says so, as it does for a switch.
  stored: {
    label: "held on the controller",
    tone: "warn",
    note: "The controller holds this configuration as the device's own record, written before the device arrived. No device has reported anything yet: the controller delivers the configuration when the device first connects, and this row is promoted once the controller reports the device running it.",
  },
};

const PUSH_KIND: Record<components["schemas"]["DevicePushKind"], string> = {
  site_apply: "the site's configuration",
  carried_seed: "the carry-over seed",
  carried_apply: "a carried configuration",
  switch_ports: "switch ports",
  // Restates ADR-0394 decision 1a: Zenith's request carries nothing, and the cell re-sends the applied document.
  // Restates ADR-0485 decision 3a: so a re-fetch that did not answer `accepted` is never marked (`pushOf`).
  refetch: "a re-fetch, which has the controller re-send the configuration already applied",
  carried_record: "the carry-over seed, written as the device's record on the controller before it arrived",
  // Restates ADR-0276 decision 4a: the operator's apply, not the seed, wrote this record.
  generated_record: "a plan an operator applied, written as the device's record on the controller before it arrived",
  // Restates ADR-0253 decision 3a: one access point's own document, and this device alone told.
  device_apply: "a plan applied to this access point alone",
};

/** A push state this build of the page does not know (#1245).
 *
 * `PUSH` is exhaustive over the contract's named list, `DevicePushState`, on purpose: a state the
 * contract *declares* and this map lacks fails the typecheck. What the typecheck cannot tell apart
 * is a value outside the list, which the field admits since ADR-0511 (it is the six or any string),
 * and there are two ways one arrives. A tab left open across a release is
 * running a bundle built against an earlier contract, and meets a declared state it predates.
 * And the API serves a stored state its own contract does not declare, as stored. Before this
 * `PUSH[p.state]` was `undefined` for either, reading `w.note` threw during render, and the whole
 * device list failed to draw for one row.
 *
 * **The word is the recorded word, marked, and never a known state's.** Not `unknown` or
 * `no answer`, which is a declared state with its own meaning (#512). Not blank, which is how
 * a device that was never written to reads. Warned and not critical, because nothing is known
 * to have gone wrong with the send; and never green, because nothing is known to have gone
 * right.
 *
 * **Own keys only.** `PUSH["constructor"]` is `Object.prototype`'s, not a state: a plain
 * index would hand back a function and draw an empty pill. `PUSH_KIND` had the same hole
 * behind its `??`, which only catches a missing key.
 */
function unrecognisedPush(state: string): { label: string; tone?: Tone; note?: string } {
  return {
    // Restates ADR-0322 and ADR-0511 decision 5a — a client of the API tolerates an enum value it does not know: the
    // stored word is shown as recorded and marked as unrecognised, never drawn as one of the
    // declared six and never left blank. The tooltip says what to do and not why; the two
    // routes are above.
    label: state ? `unrecognized: ${state}` : "unrecognized",
    tone: "warn",
    note: "This page does not recognize the state recorded for this send, so it shows the recorded word and cannot say what it means. Reload the page: a newer version may know it.",
  };
}

/** The four outcomes the page marks and ADR-0485 can unmark. An unrecognised state stays
 * warned (ADR-0322): nothing is known about it, so nothing is known to have settled. `stored`
 * is a declared warned state with its own resolution (ADR-0227 decision 5a), and the server never
 * says it settled, so it is not here either. */
const UNMARKABLE = new Set(["failed", "refused", "unknown", "sending"]);

/** Whether the site's convergence read says this device's latest push no longer describes it
 * (ADR-0485 decision 4b). The server decides it — online, and `synchronized` or carried-steady —
 * and the page only reads it: absent, or a read not yet answered, is never settled. */
function settledOf(serial: string, convergence?: ReturnType<typeof useConvergence>): boolean {
  const row = convergence?.data && firstBy(convergence.data.devices, "serial").get(serial);
  return row?.last_push_settled === true;
}

/** Where the last write got to, in words — and, since ADR-0485, whether it still describes the
 * device. `convergence` is the site's convergence read the list and the head already load; without
 * it every marked outcome stays marked, which is the fail-safe direction. */
export function pushOf(d: Device, convergence?: ReturnType<typeof useConvergence>): StateWords | undefined {
  const p = d.last_push;
  if (!p) return undefined;
  // The API's types say `p.state` is one of the six or any other string (ADR-0511), and this
  // reads what arrived: a key of `PUSH`, or `unrecognisedPush`. The `call` form
  // and not `Object.hasOwn`, so the one function that must not throw does not depend on a
  // built-in an older browser lacks.
  const own = (map: object, key: string) => Object.prototype.hasOwnProperty.call(map, key);
  const state: string = p.state ?? "";
  const w = own(PUSH, state) ? PUSH[state as keyof typeof PUSH] : unrecognisedPush(state);
  const what = wordFor(PUSH_KIND, p.kind);
  // The cell's own words reach the operator unchanged (§17.1). They are the
  // most useful thing on a refusal, so they lead the tooltip.
  const detail = p.detail ? `${p.detail}\n\n` : "";
  // The state's own sentence comes last, where a state has one: the kind and the
  // actor say what was sent and who sent it, and this says what the word means.
  // Only `accepted` carries one — it is the state whose English overclaims.
  const note = w.note ? `\n\n${w.note}` : "";
  // Restates ADR-0223 decision 1 (`1b`) and decision 2 (`2c`) — the one `accepted` that is the
  // device's own report says so, in the tooltip and not in the word. `resolved_at` is present only
  // where a resolution moved the row — `ResolveSwitchPush` for a switch, and since ADR-0227
  // decision 5a `ResolveCarriedRecord` for a carried access point's record, and since ADR-0276
  // decision 4a `ResolveGeneratedRecord` for the record an operator's apply wrote — so this sentence
  // appears exactly where the device's own report was established and nowhere else. Absent is not
  // a denial, so no sentence is added to the rows that lack it. "The device's", not "the switch's",
  // because the row is now either's.
  const resolved = p.resolved_at
    ? "\n\nThis one is also the device's own report: it was confirmed running what was sent, read from its controller after the send."
    : "";
  const title = `${detail}${what}, by ${p.actor}${note}${resolved}`;
  // Restates ADR-0485 decisions 1a, 2a and 3a (Accepted 2026-10-27, ruled on #2047) — a marked
  // outcome that no longer describes the device is drawn **settled**, neutral, and never as an error,
  // and it is never drawn blank (`—` is *never written to*, which this device was). Two routes: the
  // convergence read says the device has settled since (online, and `synchronized` or running what
  // its controller holds with nothing outstanding), or the write was the unattended re-fetch, which
  // carries no configuration and leaves the device's state to the Running column. **What the write
  // got to stays in the tooltip**, word, kind, actor, time and the controller's own words, because
  // the colour is what moved and nothing else. A `switch_ports` push is never settled here: the
  // server never says so for one, and its `unknown` resolves on the switch's own report (ADR-0074
  // decision 2).
  if (UNMARKABLE.has(state) && p.kind !== "switch_ports" && (p.kind === "refetch" || settledOf(d.serial, convergence))) {
    const at = p.finished_at ?? p.started_at;
    const when = at ? ` on ${new Date(at).toLocaleString()}` : "";
    const why = p.kind === "refetch"
      ? "The re-fetch is unattended and Zenith's request carries no configuration, so it changed nothing on the device. Whether the device is running what its site approved is the Running state."
      : "The device has since read online and running what its controller holds, with nothing outstanding, so this no longer describes it.";
    return {
      word: "settled",
      title: `Zenith's last write to this device${when} ended "${w.label}". ${why}\n\n${title}`,
    };
  }
  return { word: w.label, tone: w.tone, title };
}

/** How many client devices an access point reports, as a link to them (#248).
 *
 * The three answers are kept apart because two of them look alike. A count,
 * zero included, is what the access point's last state report says. "no
 * answer" is an online access point the cell holds no report for, or one the
 * read failed on: nothing is known, and rendering that as 0 is how an outage
 * reads as a quiet night. An offline access point was not asked (§9.4) and
 * the Status column already says so, so this cell says nothing. A switch has
 * no wireless clients and says nothing either. */
export function ClientsCell({ device, clients }: { device: Device; clients: ReturnType<typeof useClients> }) {
  if (device.category !== "ap") return <span className="muted" title="A switch has no wireless clients.">—</span>;
  const ap = clients.data && firstBy(clients.data.access_points, "serial").get(device.serial);
  if (!ap) return <span className="muted">—</span>;
  // The same marking rule as every other state on the row (#830): this is a
  // warn, so it is the pill with its dot, and not a second hand-rolled tag.
  if (ap.state === "unanswered") return <Pill tone="warn" title={ap.detail}>no answer</Pill>;
  if (ap.state === "offline") return <span className="muted" title="Offline access points are not asked.">—</span>;
  return (
    <ScopeLink to="/operate/wireless" facets={{ serial: device.serial }}
      title={`The client devices ${device.name} reports.`}>{ap.clients ?? 0}</ScopeLink>
  );
}
